import { useEffect, useState } from 'react';
import {
  Button, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { fetchReceivingDetail } from '../api/purchasing';
import type { ReceivingDetail } from '../api/types';
import { t } from '../lib/i18n';
import ErrorRetry from './ErrorRetry';
import Modal from './Modal';
import TableSkeleton from './TableSkeleton';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });
const fmtQty = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 });

interface Props {
  sid: number;
  onum: number;
  receiveNo: number;
  onClose: () => void;
  onOpenPo?: (sid: number, onum: number) => void;
}

export default function ReceivingDetailModal({ sid, onum, receiveNo, onClose, onOpenPo }: Props) {
  const [data, setData] = useState<ReceivingDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => {
    setErr(null);
    setData(null);
    fetchReceivingDetail(sid, onum, receiveNo)
      .then(setData)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, [sid, onum, receiveNo]);

  return (
    <Modal
      onClose={onClose}
      ariaLabel={t.purchasing.receptions.detail.title}
      role="dialog"
      panelClassName="max-w-4xl p-5 space-y-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <Title>{t.purchasing.receptions.detail.title}</Title>
          <Text className="text-tremor-content text-sm">
            OC #{onum} · Recep #{receiveNo} · Tienda {sid}
          </Text>
        </div>
        <div className="flex gap-2">
          {onOpenPo && (
            <Button variant="secondary" onClick={() => onOpenPo(sid, onum)}>Ver OC</Button>
          )}
          <Button variant="light" onClick={onClose}>{t.common.close}</Button>
        </div>
      </div>

      {err && <ErrorRetry message={err} onRetry={load} />}
      {!err && !data && <TableSkeleton rows={6} cols={5} />}

      {data && (
        <>
          <Card>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <Info label={t.purchasing.receptions.cols.date} value={data.header.xfr_stk} />
              <Info label={t.purchasing.receptions.cols.sup}  value={`${data.header.sup_id} · ${data.header.sup_name}`} />
              <Info label={t.purchasing.receptions.cols.recvNo} value={String(data.header.receive_no)} />
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Línea</TableHeaderCell>
                  <TableHeaderCell>Parte</TableHeaderCell>
                  <TableHeaderCell>Descripción</TableHeaderCell>
                  <TableHeaderCell className="text-right">Cant. rec</TableHeaderCell>
                  <TableHeaderCell className="text-right">Costo unit.</TableHeaderCell>
                  <TableHeaderCell className="text-right">Flete</TableHeaderCell>
                  <TableHeaderCell className="text-right">Subtotal</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.lines.map((ln, i) => (
                  <TableRow key={`${ln.line}-${ln.part}-${i}`}>
                    <TableCell>{ln.line}</TableCell>
                    <TableCell className="font-medium">{ln.part}</TableCell>
                    <TableCell>{ln.pdesc}</TableCell>
                    <TableCell className="text-right">{fmtQty.format(ln.qtyrec)}</TableCell>
                    <TableCell className="text-right">{fmtMxn.format(ln.pcost_mxn)}</TableCell>
                    <TableCell className="text-right">{fmtMxn.format(ln.frght_mxn)}</TableCell>
                    <TableCell className="text-right">{fmtMxn.format(ln.ext_mxn)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </Modal>
  );
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong font-semibold mt-0.5">{value}</div>
    </div>
  );
}
